import React from 'react';
import { Tag } from 'antd';
import { QueueBar } from './types';
import { reasonLabels, sharedBarCutLabel } from './queueClassification';

interface Props {
	bars: QueueBar[];
}

/**
 * One row per physical bar behind a queue group.
 *
 * A group is keyed by job, material and cut length, not by bar, so it can hold
 * a usable bar and an unusable one at the same time. This is the screen where
 * the operator picks which bar to cut, so the unusable ones have to say so here
 * rather than only being counted on the queue card.
 */
const QueueBarList: React.FC<Props> = ({ bars }) => {
	if (!bars?.length) return null;

	return (
		<div className="flex flex-col gap-2" data-testid="fastlane-cut-bar-list">
			{bars.map((bar) => {
				const reasons = reasonLabels(bar.blocking_reason_codes);
				// The row is already one bar, so the chip does not repeat which bar
				// it is — it names who else is waiting on it. Every one of them, not
				// a sample: this is the screen the operator decides on, and "one of
				// three" would send them back out to find the other two.
				const otherCuts = bar.other_pending_cuts ?? [];
				return (
					<div
						key={bar.material_length_id ?? bar.friendly_id}
						data-testid="fastlane-cut-bar-row"
						data-bar-actionable={bar.actionable ? 'true' : 'false'}
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: 10,
							padding: '10px 14px',
							borderRadius: 8,
							border: '1px solid #f0f0f0',
							borderLeft: `4px solid ${bar.actionable ? '#52c41a' : '#ff4d4f'}`,
							background: bar.actionable ? '#fff' : '#fff2f0',
							fontSize: 16,
						}}
					>
						<strong style={{ minWidth: 120 }}>{bar.friendly_id ?? '—'}</strong>
						<span style={{ color: '#595959' }}>{bar.status}</span>
						<span style={{ color: '#8c8c8c' }}>
							{bar.current_length} in left
						</span>
						{bar.pending_cuts_remaining > 0 ? (
							<span style={{ color: '#595959' }}>
								{bar.pending_cuts_remaining} to cut
							</span>
						) : (
							<span style={{ color: '#8c8c8c' }}>nothing left to cut</span>
						)}
						{reasons.map((reason) => (
							<Tag
								key={reason}
								color="red"
								style={{ fontSize: 14, marginInlineEnd: 0 }}
							>
								{reason}
							</Tag>
						))}
						{otherCuts.length > 0
							? otherCuts.map((cut, index) => (
									<Tag
										key={cut.order_line_item_id ?? `stock-${index}`}
										data-testid="fastlane-cut-bar-shared-cut"
										color="purple"
										style={{ fontSize: 14, marginInlineEnd: 0 }}
									>
										Also cutting: {sharedBarCutLabel(cut)}
									</Tag>
							  ))
							: bar.other_pending_groups > 0 && (
									<Tag
										color="purple"
										style={{ fontSize: 14, marginInlineEnd: 0 }}
									>
										+{bar.other_pending_groups} other job
										{bar.other_pending_groups > 1 ? 's' : ''} on this bar
									</Tag>
							  )}
					</div>
				);
			})}
		</div>
	);
};

export default QueueBarList;
